// Page /claude — OneBotAds comme connecteur Claude.
//
// Raison d'être : une page à part, hors tableau de bord, avec une seule
// fonction — donner à Claude accès aux web services de l'utilisateur, et
// laisser celui-ci connecter chaque régie avec SA propre authentification.
// Elle sert aussi de page de documentation annoncée par les métadonnées
// OAuth du serveur (service_documentation), donc lisible sans être connecté.
//
// Ce qu'elle ne fait pas : lancer le flux OAuth vers Claude. C'est Claude qui
// l'initie quand l'utilisateur ajoute le connecteur ; cette page lui donne
// l'adresse à saisir et, si une session est ouverte, la transmet à la page
// de consentement pour éviter une ressaisie.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ConnectedPlatforms from "../Dashboard/ConnectedPlatforms";
import { getAuthToken } from "../../utils/auth";
import { rememberReturnTo } from "../../utils/afterPlatformConnect";

const API_ORIGIN = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, "");
const MCP_URL = `${API_ORIGIN}/api/mcp`;

const PLATFORMS = [
  { key: "meta", label: "Meta (Facebook, Instagram)" },
  { key: "tiktok", label: "TikTok Ads" },
  { key: "snapchat", label: "Snapchat Ads" },
  { key: "linkedin", label: "LinkedIn Ads" },
  { key: "google", label: "Google Ads" },
  { key: "reddit", label: "Reddit Ads" },
];

const styles = {
  page: { maxWidth: 960, margin: "0 auto", padding: "40px 20px 80px", fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", color: "#172033" },
  eyebrow: { letterSpacing: ".08em", textTransform: "uppercase", fontSize: 12, color: "#5b6478", margin: 0 },
  h1: { fontSize: 32, margin: "6px 0 10px", lineHeight: 1.15 },
  lead: { fontSize: 17, color: "#3a4457", margin: "0 0 28px", maxWidth: 640 },
  card: { background: "#fff", border: "1px solid #e3e8f2", borderRadius: 14, padding: "22px 24px", marginBottom: 20 },
  h2: { fontSize: 18, margin: "0 0 12px" },
  url: { display: "flex", gap: 10, alignItems: "center", background: "#f4f6fb", borderRadius: 8, padding: "10px 12px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 14 },
  btn: { border: 0, borderRadius: 8, background: "#2f6bff", color: "#fff", padding: "8px 14px", fontWeight: 600, cursor: "pointer" },
  btnGhost: { border: "1px solid #cfd6e4", borderRadius: 8, background: "#fff", color: "#172033", padding: "8px 14px", fontWeight: 600, cursor: "pointer", textDecoration: "none", display: "inline-block" },
  steps: { margin: 0, paddingLeft: 20, color: "#3a4457" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, margin: "12px 0 0" },
  chip: { background: "#f4f6fb", borderRadius: 8, padding: "10px 12px", fontSize: 14 },
  fine: { color: "#7a8397", fontSize: 13 },
  warn: { background: "#fff7e6", border: "1px solid #ffd58a", borderRadius: 10, padding: "12px 14px", color: "#6b4a00", fontSize: 14 },
};

const ClaudeConnectors = () => {
  const [copied, setCopied] = useState(false);
  const token = getAuthToken();

  // Toute connexion de plateforme lancée depuis cette page doit y revenir.
  useEffect(() => { rememberReturnTo("/claude"); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* le presse-papiers peut être refusé : l'URL reste visible */ }
  };

  return (
    <main style={styles.page}>
      <p style={styles.eyebrow}>OneBotAds pour Claude</p>
      <h1 style={styles.h1}>Votre kit media buying, dans Claude</h1>
      <p style={styles.lead}>
        Ajoutez OneBotAds comme connecteur : Claude lit vos campagnes, vos statistiques et vos rapports,
        crée vos annonces en brouillon et pilote vos règles — sur <b>votre</b> compte, avec les plateformes
        que <b>vous</b> avez connectées.
      </p>

      <section style={styles.card}>
        <h2 style={styles.h2}>1. Ajouter le connecteur dans Claude</h2>
        <div style={styles.url}>
          <span style={{ flex: 1, wordBreak: "break-all" }}>{MCP_URL}</span>
          <button type="button" style={styles.btn} onClick={copy}>{copied ? "Copié" : "Copier"}</button>
        </div>
        <ol style={{ ...styles.steps, marginTop: 14 }}>
          <li>Dans Claude : <b>Paramètres → Connecteurs → Ajouter un connecteur personnalisé</b>.</li>
          <li>Collez l'adresse ci-dessus, puis <b>Connecter</b>.</li>
          <li>Claude ouvre une page OneBotAds : identifiez-vous et autorisez. Vos identifiants restent sur onebotads.net.</li>
        </ol>
      </section>

      <section style={styles.card}>
        <h2 style={styles.h2}>2. Connecter vos plateformes publicitaires</h2>
        <p style={{ margin: "0 0 6px", color: "#3a4457" }}>
          Chaque régie a sa propre authentification. Claude n'utilisera que celles que vous connectez ici.
        </p>
        <div style={styles.grid}>
          {PLATFORMS.map((p) => <div key={p.key} style={styles.chip}>{p.label}</div>)}
        </div>

        {token ? (
          <div style={{ marginTop: 18 }}>
            <ConnectedPlatforms />
          </div>
        ) : (
          <div style={{ marginTop: 18, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Link to="/login" style={styles.btnGhost} onClick={() => { try { sessionStorage.setItem("postLoginRedirect", "/claude"); } catch (e) { /* ignoré */ } }}>
              Se connecter pour gérer mes plateformes
            </Link>
            <span style={styles.fine}>Pas encore de compte ? <Link to="/signup">Créer un compte</Link></span>
          </div>
        )}
      </section>

      <section style={styles.card}>
        <h2 style={styles.h2}>Depuis Claude Code, ou un autre client MCP</h2>
        <p style={{ margin: "0 0 10px", color: "#3a4457" }}>
          Le même connecteur s'ajoute en une commande dans Claude Code :
        </p>
        <div style={styles.url}>
          <span style={{ flex: 1, wordBreak: "break-all" }}>{`claude mcp add --transport http onebotads ${MCP_URL}`}</span>
        </div>
        <p style={{ ...styles.fine, marginTop: 10 }}>
          Tout client MCP qui gère le transport HTTP et l'authentification OAuth 2.1 (PKCE, enregistrement dynamique)
          peut utiliser cette adresse. Les métadonnées de découverte sont publiées sous
          <code> /.well-known/oauth-authorization-server</code> et <code>/.well-known/oauth-protected-resource/api/mcp</code>.
        </p>
      </section>

      <section style={styles.card}>
        <h2 style={styles.h2}>Ce que Claude peut faire</h2>
        <ul style={styles.steps}>
          <li>Lire campagnes, ensembles et annonces sur toutes vos plateformes connectées.</li>
          <li>Suivre dépenses, CTR, CPC, ROAS, attribution first-party et Conversions API.</li>
          <li>Créer des campagnes — <b>en brouillon</b> par défaut, rien ne diffuse sans votre décision.</li>
          <li>Mettre en pause ou réactiver selon vos seuils, gérer vos règles d'automatisation.</li>
          <li>Générer des visuels, exploiter la bibliothèque créative et les données de vos boutiques.</li>
        </ul>
        <p style={{ ...styles.warn, marginTop: 14 }}>
          Claude n'agit que sur le compte OneBotAds avec lequel vous vous êtes identifié. Vous pouvez révoquer
          l'accès à tout moment depuis les paramètres de Claude.
        </p>
      </section>
    </main>
  );
};

export default ClaudeConnectors;
